import { MARKETPLACE_INVENTORY_PUSH_PROVIDERS } from "@redotech/redo-inventory-common/provider/marketplace-inventory-push";
import { locationIdFormat } from "@redotech/redo-inventory-id";
import { unaryRpc, unaryStreamRpc } from "@redotech/rpc/definition";
import * as z from "zod";

const SalesChannelProviderSchema = z.enum(MARKETPLACE_INVENTORY_PUSH_PROVIDERS);

const SalesChannelInventoryLocationSchema = z.object({
  id: locationIdFormat.schema,
  name: z.string(),
  /** WMS owns this location's on-hand, so it can't be imported. */
  wmsOwned: z.boolean(),
});

const SalesChannelInventorySchema = z.object({
  provider: SalesChannelProviderSchema,
  name: z.string(),
  pushEnabled: z.boolean(),
  /** False for a channel Redo can push to but not import on-hand from. */
  importSupported: z.boolean(),
  locations: z.array(SalesChannelInventoryLocationSchema),
});

export type SalesChannelInventory = z.infer<typeof SalesChannelInventorySchema>;

/** Connected sales channels Redo can push inventory to, with their locations. */
export const getSalesChannelInventory = unaryRpc({
  name: "getSalesChannelInventory" as const,
  input: z.object({}),
  output: z.object({
    /** Import and push need IMS on. */
    imsActive: z.boolean(),
    /** IMS is still turning on, so import and turning push on wait for it. */
    activating: z.boolean(),
    channels: z.array(SalesChannelInventorySchema),
  }),
});

export const setSalesChannelInventoryPush = unaryRpc({
  name: "setSalesChannelInventoryPush" as const,
  input: z.object({
    provider: SalesChannelProviderSchema,
    enabled: z.boolean(),
  }),
  output: z.object({ pushEnabled: z.boolean() }),
  mock: (_generated, input) => ({ pushEnabled: input.enabled }),
});

const SalesChannelInventoryImportSchema = z.object({
  locationId: locationIdFormat.schema,
  status: z.enum(["running", "done", "failed"]),
  /** Waiting on the channel's export, applying it, then checking Redo's other stocked products. */
  phase: z.enum(["export", "apply", "reconcile"]),
  /** Listings the channel has exported so far. */
  exportedCount: z.number(),
  processedCount: z.number(),
  checkedCount: z.number(),
  adjustedCount: z.number(),
  zeroedCount: z.number(),
  conflictCount: z.number(),
  /** The current phase's total, estimated during the export; 0 while unknown. */
  total: z.number(),
});

export type SalesChannelInventoryImport = z.infer<
  typeof SalesChannelInventoryImportSchema
>;

/**
 * Progress of the channel's imports that are running, plus any that finish
 * while watching. Ends once none are running.
 */
export const watchSalesChannelInventoryImports = unaryStreamRpc({
  name: "watchSalesChannelInventoryImports" as const,
  input: z.object({
    provider: SalesChannelProviderSchema,
    /** Seen running before a reconnect, so their finish is still reported. */
    watchingLocationIds: z.array(locationIdFormat.schema).nullish(),
  }),
  output: z.object({ imports: z.array(SalesChannelInventoryImportSchema) }),
});

/** Starts setting a location's on-hand to the channel's. */
export const importSalesChannelInventory = unaryRpc({
  name: "importSalesChannelInventory" as const,
  input: z.object({
    provider: SalesChannelProviderSchema,
    locationId: locationIdFormat.schema,
  }),
  output: z.object({ alreadyRunning: z.boolean() }),
});
